import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchClinicById } from "../../store/thunks/clinicThunk";
import { BuildingOffice2Icon } from "@heroicons/react/24/outline";
import { logInfo, logError } from "../../utils/logger";
import { Formik, Form, Field } from "formik";

function ClinicDetails({ clinic, onUpdateClinic, editMode = false }) {
  const dispatch = useDispatch();
  const {
    status: isLoading,
    error,
    selectedClinic: clinicData,
  } = useSelector((state) => state.clinics);
  const [saveStatus, setSaveStatus] = useState('initial');

  console.log("clinicdata:", clinicData);

  useEffect(() => {
    if (clinic?._id && !clinicData) {
      dispatch(fetchClinicById(clinic._id))
        .unwrap()
        .then((fetchedClinic) => {
          logInfo("Clinic details fetched successfully", {
            clinicId: fetchedClinic._id,
          });
        })
        .catch((error) => {
          logError("Failed to fetch clinic details", {
            error,
            clinicId: clinic._id,
          });
        });
    }
  }, [dispatch, clinic, clinicData]);

  if (isLoading === "loading") {
    logInfo("Loading clinic details", { clinicId: clinic?._id });
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-sm text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    logError("Error displaying clinic details", {
      clinicId: clinic?._id,
      error,
    });
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-sm text-red-600">Error: {error}</div>
      </div>
    );
  }

  if (!clinicData && !clinic) {
    logInfo("Clinic not found", { clinicId: clinic?._id });
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-sm text-gray-600">Clinic not found.</div>
      </div>
    );
  }

  const currentClinic = clinicData || clinic;

  logInfo("Rendering clinic details", {
    clinicId: currentClinic._id,
    clinicName: currentClinic.name,
  });

  const formatHours = (hours) => {
    if (!hours || !hours.open || !hours.close) {
      return "Closed";
    }
    return `${hours.open} - ${hours.close}`;
  };

  const handleSubmit = async (values) => {
    setSaveStatus('saving');
    try {
      const updatedValues = {
        ...values,
        paymentMethods: values.paymentMethods?.map((value) =>
          currentClinic.paymentMethods?.find(
            (method) => method.value === value
          )
        ),
      };
      console.log("Updating with these values:", updatedValues);
      await onUpdateClinic(updatedValues);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('initial'), 2000);
    } catch (error) {
      setSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Clinic Details</h2>
          <p className="text-sm text-gray-500 mt-1">Basic clinic information and settings</p>
        </div>
      </div>

      <Formik
        initialValues={currentClinic}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values }) => (
          <Form className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Basic Information</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Clinic Name *
                  </label>
                  {editMode ? (
                    <Field
                      name="name"
                      type="text"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.name}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  {editMode ? (
                    <Field
                      name="email"
                      type="email"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.email}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  {editMode ? (
                    <Field
                      name="phoneNumber"
                      type="tel"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.phoneNumber}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  {editMode ? (
                    <Field
                      name="website"
                      type="url"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                      {values.website || 'Not specified'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Address</h3>
              {editMode ? (
                <div className="space-y-3">
                  <Field
                    name="address.street"
                    type="text"
                    placeholder="Street Address"
                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Field
                      name="address.city"
                      type="text"
                      placeholder="City"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                    <Field
                      name="address.province"
                      type="text"
                      placeholder="Province"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                    <Field
                      name="address.postalCode"
                      type="text"
                      placeholder="Postal Code"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                    <Field
                      name="address.country"
                      type="text"
                      placeholder="Country"
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>
                </div>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                  {values.address?.street && (
                    <>
                      {values.address.street}<br />
                      {values.address.city}, {values.address.province} {values.address.postalCode}<br />
                      {values.address.country}
                    </>
                  ) || 'No address specified'}
                </div>
              )}
            </div>

            {/* Hours of Operation */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Hours of Operation</h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(values.hoursOfOperation || {}).map(([day, hours]) => (
                  <div key={day} className="flex items-center space-x-2">
                    <span className="w-20 text-sm font-medium text-gray-700">{day}:</span>
                    {editMode ? (
                      <div className="flex items-center space-x-1 flex-1">
                        <Field
                          name={`hoursOfOperation.${day}.open`}
                          type="time"
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                        <span className="text-gray-500">-</span>
                        <Field
                          name={`hoursOfOperation.${day}.close`}
                          type="time"
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-gray-900">{formatHours(hours)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Methods */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-900">Payment Methods</h3>
              {editMode ? (
                <Field
                  name="paymentMethods"
                  as="select"
                  multiple
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  size="4"
                >
                  {currentClinic.paymentMethods?.map((method) => (
                    <option key={method._id} value={method.value}>
                      {method.englishDescription}
                    </option>
                  ))}
                </Field>
              ) : (
                <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-sm">
                  {currentClinic.paymentMethods?.length > 0
                    ? currentClinic.paymentMethods
                        .map((method) => method.englishDescription)
                        .join(", ")
                    : 'No payment methods specified'}
                </div>
              )}
            </div>

            {/* Save Button - only show when in edit mode */}
            {editMode && (
              <div className="flex justify-end pt-4 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={saveStatus === 'saving'}
                  className={`inline-flex justify-center px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                    saveStatus === 'saving'
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : saveStatus === 'saved'
                      ? 'bg-green-600 text-white'
                      : saveStatus === 'error'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {saveStatus === 'saving' ? 'Saving...' :
                    saveStatus === 'saved' ? 'Saved!' :
                    saveStatus === 'error' ? 'Error' : 'Save Changes'}
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default ClinicDetails;
